<template>
    <themed-modal
        name="tour-scoring-modal"
        title="Score Tour Comps"
        size="xl"
        :show-footer="true"
        @closed="closeModal"
    >
        <!-- Attendee Selector (for demo) -->
        <div class="attendee-selector">
            <div class="attendee-selector__label">
                <strong>Scoring as:</strong>
            </div>
            <themed-select
                v-model="selectedAttendeeId"
                :options="attendeeOptions"
                placeholder="Select an attendee"
                @input="onAttendeeChange"
            />
        </div>

        <div class="scoring-progress">
            <div class="scoring-progress__bar">
                <div 
                    class="scoring-progress__fill"
                    :style="{ width: progressPercentage + '%' }"
                ></div>
            </div>
            <div class="scoring-progress__text">
                {{ completedScores }} of {{ totalScores }} scores completed ({{ progressPercentage }}%)
            </div>
        </div>

        <div class="ksd-weights-section">
            <div class="section-header">
                <h4>Your KSD Weights</h4>
                <themed-button
                    @click="showWeightsEditor = !showWeightsEditor"
                    size="sm"
                    variant="secondary"
                >
                    {{ showWeightsEditor ? 'Hide' : 'Edit Weights' }}
                </themed-button>
            </div>

            <div v-if="showWeightsEditor" class="weights-editor">
                <div
                    v-for="weight in weights"
                    :key="weight.ksd_id"
                    class="weight-item"
                >
                    <label class="weight-item__label">
                        {{ weight.ksd.driver }}
                    </label>
                    <div class="weight-item__slider">
                        <themed-slider
                            v-model="weight.weight"
                            :min="0"
                            :max="100"
                            :step="1"
                        />
                        <span class="weight-item__value">{{ weight.weight }}</span>
                    </div>
                </div>
                <themed-button
                    @click="saveWeights"
                    variant="default"
                    :disabled="savingWeights"
                >
                    {{ savingWeights ? 'Saving...' : 'Save Weights' }}
                </themed-button>
            </div>
        </div>

        <div class="comp-navigation">
            <themed-button
                @click="previousComp"
                variant="secondary"
                :disabled="currentCompIndex === 0"
            >
                ← Previous
            </themed-button>
            <span class="comp-counter">
                Comp {{ currentCompIndex + 1 }} of {{ comps.length }}
            </span>
            <themed-button
                @click="nextComp"
                variant="secondary"
                :disabled="currentCompIndex === comps.length - 1"
            >
                Next →
            </themed-button>
        </div>

        <div v-if="currentComp" class="comp-details">
            <h4 class="comp-name">
                {{ getCompName(currentComp) }}
            </h4>
            <div v-if="getCompBuilding(currentComp)" class="comp-info">
                <p v-if="getCompBuilding(currentComp).address_line_1">
                    {{ getCompBuilding(currentComp).address_line_1 }}
                </p>
                <p v-if="getCompBuilding(currentComp).city">
                    {{ getCompBuilding(currentComp).city }}, {{ getCompBuilding(currentComp).state }}
                </p>
            </div>
        </div>

        <div v-if="currentComp" class="scoring-form">
            <div
                v-for="ksd in ksds"
                :key="ksd.id"
                class="score-item"
            >
                <label class="score-item__label">
                    {{ ksd.driver }}
                </label>
                <div class="score-item__slider">
                    <themed-slider
                        v-model="compScores[ksd.id]"
                        :min="0"
                        :max="100"
                        :step="1"
                        @change="saveScore(ksd.id)"
                    />
                    <span class="score-item__value">{{ compScores[ksd.id] }}</span>
                </div>
                <div class="score-item__status">
                    <span v-if="scoreSaving[ksd.id]" class="saving">Saving...</span>
                    <span v-else-if="scoreSaved[ksd.id]" class="saved">✓</span>
                </div>
            </div>
        </div>

        <div class="quick-actions">
            <themed-button
                @click="saveAllScores"
                variant="default"
                :disabled="savingAll"
            >
                {{ savingAll ? 'Saving All...' : 'Save All Scores' }}
            </themed-button>
        </div>

        <template #footer>
            <themed-button @click="closeModal" variant="secondary">
                Close
            </themed-button>
            <themed-button
                v-if="isComplete"
                @click="completeScoring"
                variant="default"
            >
                Complete Scoring
            </themed-button>
        </template>
    </themed-modal>
</template>

<script>
import axios from 'axios';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedSlider from '../ThemedSlider.vue';
import ThemedSelect from '../ThemedSelect.vue';

export default {
    name: 'TourScoring',

    components: {
        ThemedModal,
        ThemedButton,
        ThemedSlider,
        ThemedSelect,
    },

    props: {
        tour: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            selectedAttendeeId: null,
            attendees: [],
            weights: [],
            ksds: [],
            comps: [],
            currentCompIndex: 0,
            compScores: {},
            scoreSaving: {},
            scoreSaved: {},
            showWeightsEditor: false,
            savingWeights: false,
            savingAll: false,
            loading: false,
            completedScores: 0,
            totalScores: 0,
        };
    },

    computed: {
        currentComp() {
            return this.comps[this.currentCompIndex] || null;
        },
        progressPercentage() {
            return this.totalScores > 0 
                ? Math.round((this.completedScores / this.totalScores) * 100)
                : 0;
        },
        isComplete() {
            return this.completedScores === this.totalScores && this.totalScores > 0;
        },
        attendeeOptions() {
            return this.attendees.map(attendee => ({
                value: attendee.id,
                label: attendee.display_name || attendee.attendee_name || attendee.attendee_email || 'Unknown Attendee'
            }));
        },
    },

    mounted() {
        this.$modal.show('tour-scoring-modal');
        if (this.tour) {
            this.loadData();
        }
    },

    watch: {
        currentCompIndex() {
            this.loadCompScores();
        },
    },

    methods: {
        closeModal() {
            this.$modal.hide('tour-scoring-modal');
        },

        getCompBuilding(comp) {
            // Try to get building from comp relationship first, then direct building
            return comp.comp?.building || comp.building || null;
        },

        getCompName(comp) {
            const building = this.getCompBuilding(comp);
            if (!building) return 'Unnamed Comp';
            return building.building_name || building.address_line_1 || 'Unnamed Comp';
        },
        
        async loadData() {
            this.loading = true;
            try {
                await Promise.all([
                    this.loadAttendees(),
                    this.loadTourDetails(),
                ]);
                
                // Set first attendee as default if available
                if (this.attendees.length > 0 && !this.selectedAttendeeId) {
                    this.selectedAttendeeId = this.attendees[0].id;
                }
                
                // Load weights and progress for selected attendee
                if (this.selectedAttendeeId) {
                    await Promise.all([
                        this.loadWeights(),
                        this.loadProgress(),
                    ]);
                }
                
                if (this.comps.length > 0) {
                    await this.loadCompScores();
                }
            } catch (error) {
                console.error('Error loading data:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load scoring data');
                }
            } finally {
                this.loading = false;
            }
        },

        async loadAttendees() {
            try {
                const response = await axios.get(`/tours/${this.tour.id}`);
                const tourData = response.data;
                this.attendees = tourData.attendees || [];
                
                console.log('Attendees loaded:', this.attendees);
                console.log('Attendee count:', this.attendees.length);
                
                if (this.attendees.length === 0) {
                    console.warn('No attendees found for this tour');
                    if (this.$toast) {
                        this.$toast.warning('No attendees found. Please invite attendees first.');
                    }
                }
            } catch (error) {
                console.error('Error loading attendees:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load attendees');
                }
            }
        },

        async onAttendeeChange() {
            // Reload weights and scores for the new attendee
            this.loading = true;
            try {
                await Promise.all([
                    this.loadWeights(),
                    this.loadProgress(),
                ]);
                if (this.comps.length > 0) {
                    await this.loadCompScores();
                }
            } catch (error) {
                console.error('Error loading attendee data:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load attendee data');
                }
            } finally {
                this.loading = false;
            }
        },

        async loadWeights() {
            if (!this.selectedAttendeeId) {
                console.warn('No attendee selected');
                return;
            }

            try {
                const response = await axios.get(`/tours/${this.tour.id}/my-weights`, {
                    params: { attendee_id: this.selectedAttendeeId }
                });
                this.weights = response.data;
                console.log('Weights loaded:', this.weights);
                console.log('Weight count:', this.weights.length);
                
                // If no weights exist, initialize them from KSDs
                if (this.weights.length === 0 && this.ksds.length > 0) {
                    console.log('No weights found, initializing from KSDs');
                    this.weights = this.ksds.map(ksd => ({
                        ksd_id: ksd.id,
                        weight: ksd.weight || 1,
                        ksd: ksd
                    }));
                    console.log('Initialized weights:', this.weights);
                }
            } catch (error) {
                console.error('Error loading weights:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load weights');
                }
            }
        },

        async loadTourDetails() {
            const response = await axios.get(`/tours/${this.tour.id}`);
            const tourData = response.data;
            
            this.comps = tourData.tour_comps || tourData.tourComps || [];
            this.ksds = tourData.project?.ksd_scenarios?.[0]?.ksds || [];
            this.totalScores = this.comps.length * this.ksds.length;
            
            console.log('Tour details loaded:', tourData);
            console.log('Comps:', this.comps);
            console.log('Comp count:', this.comps.length);
            console.log('KSDs:', this.ksds);
            console.log('KSD count:', this.ksds.length);
            console.log('Total scores expected:', this.totalScores);
        },

        async loadProgress() {
            if (!this.selectedAttendeeId) {
                this.completedScores = 0;
                return;
            }

            try {
                const response = await axios.get(`/tours/${this.tour.id}/progress`);
                const myProgress = response.data.attendee_progress?.find(
                    p => p.attendee_id === this.selectedAttendeeId
                );
                this.completedScores = myProgress ? myProgress.submitted : 0;
            } catch (error) {
                console.error('Error loading progress:', error);
                // Don't show error toast for progress, just set to 0
                this.completedScores = 0;
            }
        },

        async loadCompScores() {
            if (!this.currentComp || !this.selectedAttendeeId) return;

            console.log('Loading scores for comp:', this.currentComp);
            console.log('Current comp building:', this.currentComp.building);
            console.log('Current comp comp:', this.currentComp.comp);

            try {
                const response = await axios.get(
                    `/tours/${this.tour.id}/comps/${this.currentComp.comp_id}/my-score`,
                    { params: { attendee_id: this.selectedAttendeeId } }
                );
                
                this.compScores = {};
                response.data.forEach(score => {
                    this.compScores[score.ksd_id] = score.score;
                    this.scoreSaved[score.ksd_id] = true;
                });

                this.ksds.forEach(ksd => {
                    if (!(ksd.id in this.compScores)) {
                        this.compScores[ksd.id] = 50;
                    }
                });
            } catch (error) {
                console.error('Error loading comp scores:', error);
            }
        },

        async saveScore(ksdId) {
            if (!this.selectedAttendeeId) {
                if (this.$toast) {
                    this.$toast.error('Please select an attendee');
                }
                return;
            }

            this.scoreSaving[ksdId] = true;
            this.scoreSaved[ksdId] = false;

            try {
                await axios.post(`/tours/${this.tour.id}/score`, {
                    attendee_id: this.selectedAttendeeId,
                    comp_id: this.currentComp.comp_id,
                    ksd_id: ksdId,
                    score: this.compScores[ksdId],
                });
                
                this.scoreSaved[ksdId] = true;
                await this.loadProgress();
            } catch (error) {
                console.error('Error saving score:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to save score');
                }
            } finally {
                this.scoreSaving[ksdId] = false;
            }
        },

        async saveAllScores() {
            if (!this.currentComp || !this.selectedAttendeeId) return;

            this.savingAll = true;
            try {
                const scores = this.ksds.map(ksd => ({
                    ksd_id: ksd.id,
                    score: this.compScores[ksd.id] || 50,
                }));

                await axios.post(`/tours/${this.tour.id}/batch-score`, {
                    attendee_id: this.selectedAttendeeId,
                    comp_id: this.currentComp.comp_id,
                    scores: scores,
                });

                if (this.$toast) {
                    this.$toast.success('All scores saved successfully');
                }
                this.ksds.forEach(ksd => {
                    this.scoreSaved[ksd.id] = true;
                });
                await this.loadProgress();
            } catch (error) {
                console.error('Error saving all scores:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to save scores');
                }
            } finally {
                this.savingAll = false;
            }
        },

        async saveWeights() {
            if (!this.selectedAttendeeId) {
                if (this.$toast) {
                    this.$toast.error('Please select an attendee');
                }
                return;
            }

            this.savingWeights = true;
            try {
                await axios.put(`/tours/${this.tour.id}/my-weights`, {
                    attendee_id: this.selectedAttendeeId,
                    weights: this.weights.map(w => ({
                        ksd_id: w.ksd_id,
                        weight: w.weight,
                    })),
                });
                if (this.$toast) {
                    this.$toast.success('Weights saved successfully');
                }
                this.showWeightsEditor = false;
            } catch (error) {
                console.error('Error saving weights:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to save weights');
                }
            } finally {
                this.savingWeights = false;
            }
        },

        previousComp() {
            if (this.currentCompIndex > 0) {
                this.currentCompIndex--;
            }
        },

        nextComp() {
            if (this.currentCompIndex < this.comps.length - 1) {
                this.currentCompIndex++;
            }
        },

        completeScoring() {
            if (this.$toast) {
                this.$toast.success('Scoring completed!');
            }
            this.$emit('completed');
            this.closeModal();
        },
    },
};
</script>
